/**
 * DOM element inspection helpers for PlaywrightClient.
 *
 * @module playwright-client/element-inspection
 * @see docs/api/services/browser-session.md
 */

import type { ElementInfo } from '@chatsdet/types';
import type { Page } from 'playwright';
import type { BrowserLogger } from '../logger.js';
import type { ClickedElementInfo } from '../click-validation.js';

/**
 * Capture rich info about the DOM element at a given point.
 */
export async function captureElementAtPoint(
  page: Page,
  x: number,
  y: number
): Promise<ClickedElementInfo | null> {
  return page.evaluate(
    ({ clickX, clickY }) => {
      const el = document.elementFromPoint(clickX, clickY);
      if (!el) return null;

      // Helper: Get implicit role from tagName
      function getImplicitRole(element: Element): string | undefined {
        const tag = element.tagName.toLowerCase();
        const roleMap: Record<string, string> = {
          button: 'button',
          a: 'link',
          input: 'textbox',
          select: 'combobox',
          textarea: 'textbox',
          img: 'img',
          table: 'table',
          tr: 'row',
          td: 'cell',
          th: 'columnheader',
          ul: 'list',
          ol: 'list',
          li: 'listitem',
          nav: 'navigation',
          header: 'banner',
          footer: 'contentinfo',
          main: 'main',
          form: 'form',
          dialog: 'dialog',
        };
        return roleMap[tag];
      }

      // Helper: Find associated label for form fields
      function findAssociatedLabel(element: Element): string | undefined {
        const id = element.id;
        if (id) {
          const label = document.querySelector(`label[for="${id}"]`);
          if (label) return label.textContent?.trim();
        }
        const labelledBy = element.getAttribute('aria-labelledby');
        if (labelledBy) {
          const labelEl = document.getElementById(labelledBy);
          if (labelEl) return labelEl.textContent?.trim();
        }
        // Check if wrapped in label
        const parentLabel = element.closest('label');
        if (parentLabel) return parentLabel.textContent?.trim();
        return undefined;
      }

      // Helper: Walk up DOM to find semantic parent
      function findSemanticParent(element: Element): { role?: string; name?: string } | undefined {
        const semanticRoles = [
          'grid',
          'treegrid',
          'list',
          'listbox',
          'menu',
          'menubar',
          'dialog',
          'alertdialog',
          'form',
          'table',
          'tree',
        ];
        let current = element.parentElement;
        let depth = 0;
        while (current && depth < 5) {
          const role = current.getAttribute('role') || getImplicitRole(current);
          if (role && semanticRoles.includes(role)) {
            const labelledById = current.getAttribute('aria-labelledby');
            const name =
              current.getAttribute('aria-label') ||
              (labelledById && document.getElementById(labelledById)?.textContent?.trim());
            return { role, name: name || undefined };
          }
          current = current.parentElement;
          depth++;
        }
        return undefined;
      }

      // Helper: Check if element is in a table row
      function isInTableRow(element: Element): boolean {
        return !!element.closest('tr, [role="row"]');
      }

      // Helper: Find column header for a table cell
      function findColumnHeader(element: Element): string | undefined {
        const row = element.closest('tr, [role="row"]');
        if (!row) return undefined;
        const cells = Array.from(row.children);
        const cellEl = element.closest('td, th, [role="cell"], [role="gridcell"]');
        const cellIndex = cellEl ? cells.indexOf(cellEl as Element) : -1;
        if (cellIndex < 0) return undefined;

        const table = element.closest('table, [role="grid"], [role="treegrid"]');
        if (!table) return undefined;

        const headerRow = table.querySelector('thead tr, [role="row"]:first-child');
        if (!headerRow) return undefined;

        const headers = Array.from(headerRow.children);
        return headers[cellIndex]?.textContent?.trim();
      }

      // Helper: Get text of all cells in the same row
      function getRowText(element: Element): string | undefined {
        const row = element.closest('tr, [role="row"]');
        if (!row) return undefined;
        const cells = Array.from(row.querySelectorAll('td, th, [role="cell"], [role="gridcell"]'));
        const texts = cells.map((c) => c.textContent?.trim()).filter(Boolean);
        return texts.length > 0 ? texts.join(' | ') : undefined;
      }

      // Build result - use explicit type to satisfy TypeScript
      const result: {
        tagName: string;
        id?: string;
        className?: string;
        textContent?: string;
        role?: string;
        ariaLabel?: string;
        parentRole?: string;
        parentName?: string;
        rowIndex?: number;
        itemIndex?: number;
        columnHeader?: string;
        rowText?: string;
        dataTestId?: string;
        labelText?: string;
        placeholder?: string;
        altText?: string;
        title?: string;
      } = {
        tagName: el.tagName,
        id: el.id || undefined,
        className: typeof el.className === 'string' ? el.className || undefined : undefined,
        textContent: el.textContent?.slice(0, 100)?.trim() || undefined,
        role: el.getAttribute('role') || getImplicitRole(el) || undefined,
        ariaLabel: el.getAttribute('aria-label') || undefined,
      };

      // Data attributes
      result.dataTestId =
        el.getAttribute('data-testid') || el.getAttribute('data-test-id') || undefined;
      result.placeholder = el.getAttribute('placeholder') || undefined;
      result.altText = el.getAttribute('alt') || undefined;
      result.title = el.getAttribute('title') || undefined;

      // Row/item index
      const rowIndexAttr = el.getAttribute('aria-rowindex');
      if (rowIndexAttr) {
        const parsed = parseInt(rowIndexAttr, 10);
        if (!isNaN(parsed)) result.rowIndex = parsed;
      }
      const itemIndexAttr =
        el.getAttribute('data-item-index') || el.getAttribute('data-selection-index');
      if (itemIndexAttr) {
        const parsed = parseInt(itemIndexAttr, 10);
        if (!isNaN(parsed)) result.itemIndex = parsed;
      }

      // Label association
      result.labelText = findAssociatedLabel(el);

      // Parent context
      const parentInfo = findSemanticParent(el);
      if (parentInfo) {
        result.parentRole = parentInfo.role;
        result.parentName = parentInfo.name;
      }

      // Table cell context
      if (isInTableRow(el)) {
        result.columnHeader = findColumnHeader(el);
        result.rowText = getRowText(el);
      }

      // Clean undefined values
      Object.keys(result).forEach((key) => {
        if (result[key as keyof typeof result] === undefined) {
          delete result[key as keyof typeof result];
        }
      });

      return result;
    },
    { clickX: x, clickY: y }
  );
}

/**
 * Extract cache-relevant metadata directly from the DOM element.
 */
export async function extractElementMetadata(
  locator: ReturnType<Page['locator']>,
  ref: string,
  _logger: BrowserLogger
): Promise<ElementInfo> {
  const [box, axSnapshot, domProps] = await Promise.all([
    locator.boundingBox().catch(() => null),
    locator.evaluate((el) => {
      const computedRole = el.getAttribute('role') || el.tagName.toLowerCase();
      const computedName =
        el.getAttribute('aria-label') ||
        (el as HTMLInputElement).labels?.[0]?.textContent?.trim() ||
        el.getAttribute('title') ||
        (el as HTMLElement).innerText?.trim()?.slice(0, 120) ||
        null;
      return { role: computedRole, name: computedName };
    }).catch(() => null),
    locator.evaluate((el) => {
      const stripPrivateUseGlyphs = (value: string): string => value.replace(/[\uE000-\uF8FF]/g, '');

      const normalizeText = (value: string | null | undefined): string | undefined => {
        if (!value) return undefined;
        const cleaned = stripPrivateUseGlyphs(value.replace(/\u00A0/g, ' '));
        const trimmed = cleaned.trim();
        if (!trimmed) return undefined;
        return trimmed.replace(/\s+/g, ' ').slice(0, 120);
      };

      const collectTextSegments = (root: Element): string[] => {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_SKIP;
            if (parent.closest('[aria-hidden="true"],svg,i,[data-icon-name],.ms-Icon')) {
              return NodeFilter.FILTER_SKIP;
            }
            const nodeText = node.textContent;
            return nodeText && nodeText.trim().length > 0
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_SKIP;
          },
        });

        const segments: string[] = [];
        let current = walker.nextNode();
        while (current) {
          const normalized = normalizeText(current.textContent ?? '');
          if (normalized) {
            segments.push(normalized);
          }
          current = walker.nextNode();
        }
        return segments;
      };

      const tagName = el.tagName.toLowerCase();

      const roleFromTag = (): string | undefined => {
        if (tagName === 'button') return 'button';
        if (tagName === 'a' && (el as HTMLAnchorElement).href) return 'link';
        if (tagName === 'textarea') return 'textbox';
        if (tagName === 'select') return 'combobox';
        if (tagName === 'option') return 'option';
        if (tagName === 'img') return 'img';
        if (tagName === 'input') {
          const input = el as HTMLInputElement;
          const type = input.type?.toLowerCase() || 'text';
          if (type === 'checkbox') return 'checkbox';
          if (type === 'radio') return 'radio';
          if (type === 'range') return 'slider';
          if (type === 'button' || type === 'submit' || type === 'reset' || type === 'image') {
            return 'button';
          }
          return 'textbox';
        }
        return undefined;
      };

      const ariaLabel = normalizeText(el.getAttribute('aria-label'));
      const labelledBy = el.getAttribute('aria-labelledby');
      const labelledByText = labelledBy
        ? labelledBy
            .split(/\s+/)
            .map((id) => document.getElementById(id)?.textContent)
            .map((text) => normalizeText(text))
            .filter(Boolean)
            .join(' ')
        : undefined;
      const labelFromLabels = (el as HTMLInputElement).labels?.length
        ? normalizeText((el as HTMLInputElement).labels?.[0]?.textContent ?? null)
        : undefined;
      const labelText = labelledByText || labelFromLabels || ariaLabel;

      const placeholder = normalizeText((el as HTMLInputElement).placeholder);
      const title = normalizeText(el.getAttribute('title'));
      const altText = normalizeText((el as HTMLImageElement).alt);
      const nameAttr = normalizeText((el as HTMLInputElement).name);
      const textSegments = collectTextSegments(el);
      const visibleText =
        normalizeText(textSegments.join(' ')) || normalizeText((el as HTMLElement).innerText);
      const rawTextContent = normalizeText(el.textContent);
      const dataTestId = normalizeText(
        el.getAttribute('data-testid') || el.getAttribute('data-test-id') || el.getAttribute('data-test')
      );

      const roleAttr = normalizeText(el.getAttribute('role'));
      const role = roleAttr || roleFromTag();

      // Traverse up to find best parent for disambiguation
      const findBestParent = (): { role?: string; name?: string } | null => {
        const preferredRoles = [
          'dialog',
          'alertdialog',
          'form',
          'article',
          'region',
          'main',
          'navigation',
          'complementary',
          'banner',
          'contentinfo',
        ];
        let current = el.parentElement;
        let depth = 0;
        while (current && depth < 10) {
          const parentTag = current.tagName.toLowerCase();
          const parentRoleAttr = current.getAttribute('role');
          const parentRoleFromTag =
            parentTag === 'nav'
              ? 'navigation'
              : parentTag === 'main'
                ? 'main'
                : parentTag === 'header'
                  ? 'banner'
                  : parentTag === 'footer'
                    ? 'contentinfo'
                    : parentTag === 'aside'
                      ? 'complementary'
                      : parentTag === 'section'
                        ? 'region'
                        : parentTag === 'article'
                          ? 'article'
                          : parentTag === 'form'
                            ? 'form'
                            : parentTag === 'dialog'
                              ? 'dialog'
                              : undefined;
          const parentRole = parentRoleAttr || parentRoleFromTag;
          const parentName = normalizeText(current.getAttribute('aria-label'));
          if (parentRole && preferredRoles.includes(parentRole)) {
            return { role: parentRole, name: parentName };
          }
          // Also prefer named parents
          if (parentRole && parentName) {
            return { role: parentRole, name: parentName };
          }
          current = current.parentElement;
          depth++;
        }
        return null;
      };

      const parent = findBestParent();

      // Detect if element is inside an iframe
      const detectFrameContext = (): {
        type: 'iframe' | 'shadow' | 'named-frame';
        selector: string;
        name?: string;
        id?: string;
      } | null => {
        const win = el.ownerDocument.defaultView;
        if (!win || win === win.top) {
          return null; // Not in an iframe
        }

        // We're inside an iframe - get the frame element info
        const frameElement = win.frameElement as HTMLIFrameElement | null;
        if (!frameElement) {
          return null;
        }

        // Build the best selector for the iframe
        const frameId = frameElement.id;
        const frameName = frameElement.name;
        const frameClass = frameElement.className;

        let selector: string;
        if (frameId) {
          selector = `iframe#${frameId}`;
        } else if (frameName) {
          selector = `iframe[name="${frameName}"]`;
        } else if (frameClass && typeof frameClass === 'string') {
          // Use the first class as selector
          const firstClass = frameClass.split(/\s+/).filter(Boolean)[0];
          if (firstClass) {
            selector = `iframe.${firstClass}`;
          } else {
            selector = 'iframe';
          }
        } else {
          // Fallback: try to find index among sibling iframes
          const allIframes = frameElement.parentElement?.querySelectorAll('iframe');
          if (allIframes && allIframes.length > 1) {
            const index = Array.from(allIframes).indexOf(frameElement);
            selector = `iframe:nth-of-type(${index + 1})`;
          } else {
            selector = 'iframe';
          }
        }

        return {
          type: 'iframe',
          selector,
          name: frameName || undefined,
          id: frameId || undefined,
        };
      };

      const frameContext = detectFrameContext();

      // Capture list/selection indices
      const rawItemIndex =
        el.getAttribute('data-item-index') ?? el.getAttribute('data-selection-index');
      const itemIndex = rawItemIndex !== null ? parseInt(rawItemIndex, 10) : undefined;

      // Capture aria-rowindex for table/grid rows
      const rawAriaRowIndex = el.getAttribute('aria-rowindex');
      const ariaRowIndex = rawAriaRowIndex !== null ? parseInt(rawAriaRowIndex, 10) : undefined;

      // Capture Microsoft automation ID
      const automationId = normalizeText(el.getAttribute('data-automationid'));

      // Capture all data-* attributes for future disambiguation use
      const dataAttributes: Record<string, string> = {};
      for (const attr of el.getAttributeNames()) {
        if (attr.startsWith('data-') && !attr.startsWith('data-test')) {
          const value = el.getAttribute(attr);
          if (value && value.length <= 120) {
            dataAttributes[attr] = value;
          }
        }
      }

      // Scan for direct child <img> with alt text (for disambiguation of generic containers)
      const childImgAlt = (() => {
        const firstImgWithAlt = el.querySelector(':scope > img[alt]');
        if (firstImgWithAlt) return normalizeText((firstImgWithAlt as HTMLImageElement).alt);
        return undefined;
      })();

      return {
        role,
        ariaLabel,
        labelText,
        placeholder,
        title,
        altText,
        nameAttr,
        textContent: visibleText || rawTextContent,
        textSegments: textSegments.length > 0 ? textSegments : undefined,
        tagName,
        id: normalizeText(el.id),
        className: typeof el.className === 'string' ? normalizeText(el.className) : undefined,
        dataTestId,
        parentRole: parent?.role,
        parentName: parent?.name,
        frameContext,
        // Data attributes for disambiguation
        itemIndex: itemIndex !== undefined && !isNaN(itemIndex) ? itemIndex : undefined,
        ariaRowIndex: ariaRowIndex !== undefined && !isNaN(ariaRowIndex) ? ariaRowIndex : undefined,
        automationId,
        dataAttributes: Object.keys(dataAttributes).length > 0 ? dataAttributes : undefined,
        childImgAlt,
      };
    }),
  ]);

  const normalizeSnapshotText = (value: string | null | undefined): string | undefined => {
    if (!value) return undefined;
    const cleaned = value.replace(/[\uE000-\uF8FF]/g, '').replace(/\u00A0/g, ' ');
    const trimmed = cleaned.trim();
    if (!trimmed) return undefined;
    return trimmed.replace(/\s+/g, ' ');
  };

  const trimmedRole = axSnapshot?.role?.trim();
  // The fast name probe falls back to tagName; prefer the richer DOM role map when that happens.
  const normalizedRole =
    trimmedRole &&
    trimmedRole !== 'none' &&
    trimmedRole !== 'presentation' &&
    trimmedRole !== domProps.tagName
      ? trimmedRole
      : domProps.role;
  const trimmedName = normalizeSnapshotText(axSnapshot?.name);
  const normalizedName =
    trimmedName ||
    normalizeSnapshotText(domProps.labelText) ||
    normalizeSnapshotText(domProps.ariaLabel) ||
    normalizeSnapshotText(domProps.title) ||
    normalizeSnapshotText(domProps.textContent);

  return {
    ref,
    role: normalizedRole,
    ariaLabel: domProps.ariaLabel,
    labelText: domProps.labelText,
    placeholder: domProps.placeholder,
    title: domProps.title,
    altText: domProps.altText,
    nameAttr: domProps.nameAttr,
    dataTestId: domProps.dataTestId,
    accessibleName: normalizedName,
    textContent: domProps.textContent,
    textSegments: domProps.textSegments,
    tagName: domProps.tagName,
    id: domProps.id,
    className: domProps.className,
    parentRole: domProps.parentRole,
    parentName: domProps.parentName,
    centerX: box ? Math.round(box.x + box.width / 2) : undefined,
    centerY: box ? Math.round(box.y + box.height / 2) : undefined,
    frameContext: domProps.frameContext ?? undefined,
    // Data attributes for disambiguation
    itemIndex: domProps.itemIndex,
    ariaRowIndex: domProps.ariaRowIndex,
    automationId: domProps.automationId,
    dataAttributes: domProps.dataAttributes,
    childImgAlt: domProps.childImgAlt,
  };
}
